import type { Ics221Entry } from 'components/IAP/hooks/ics221EntryHooks';

/**
 * One ICS 221 in the Demobilization list: a resource's check-out sheet or a
 * hand-written documentation entry.
 */
export type Ics221Row =
  | {
      kind: 'checkout';
      /** The version chain and export key: the resource in incident id. */
      id: string;
      label: string;
      detail: string;
      sortAt: string;
      /** The edits made on this check-out's sheet, if any. */
      overlay?: Ics221Entry;
    }
  | {
      kind: 'entry';
      id: string;
      label: string;
      detail: string;
      sortAt: string;
      entry: Ics221Entry;
    };

type CheckoutResource = {
  resource_in_incident_id: string;
  resource_name_identifier?: string | null;
  resource_kind?: string | null;
  checkout_datetime?: string | Date | null;
};

const iso = (value: string | Date | null | undefined) =>
  value ? new Date(value).toISOString() : '';

/**
 * Check-outs and documentation entries together, most recent first. A
 * check-out's overlay rides on its check-out row rather than listing twice.
 */
export function ics221Rows(
  resources: readonly CheckoutResource[],
  entries: readonly Ics221Entry[]
): Ics221Row[] {
  const names = new Map(
    resources.map((resource) => [
      resource.resource_in_incident_id,
      resource.resource_name_identifier || resource.resource_in_incident_id,
    ])
  );
  const overlays = new Map(
    entries
      .filter((entry) => entry.source === 'checkout')
      .map((entry) => [entry.resourceInIncidentId, entry])
  );
  const checkouts: Ics221Row[] = resources
    .filter((resource) => resource.checkout_datetime)
    .map((resource) => {
      const overlay = overlays.get(resource.resource_in_incident_id);
      return {
        kind: 'checkout',
        id: resource.resource_in_incident_id,
        label:
          overlay?.fields.resourcePersonnelReleased ||
          resource.resource_name_identifier ||
          resource.resource_in_incident_id,
        detail: resource.resource_kind || '',
        sortAt: iso(resource.checkout_datetime),
        ...(overlay ? { overlay } : {}),
      };
    });
  const documented: Ics221Row[] = entries
    .filter((entry) => entry.source !== 'checkout')
    .map((entry) => ({
      kind: 'entry',
      id: entry.id,
      label:
        entry.fields.resourcePersonnelReleased ||
        entry.resourceLabel ||
        (entry.resourceInIncidentId
          ? names.get(entry.resourceInIncidentId)
          : undefined) ||
        'Documentation entry',
      detail: entry.resourceKind,
      sortAt: entry.createdAt,
      entry,
    }));
  return [...checkouts, ...documented].sort((a, b) =>
    b.sortAt.localeCompare(a.sortAt)
  );
}
